import { LEAVE_TYPE_LABELS, REIMBURSEMENT_TYPE_LABELS } from '@company/shared'
import { Check, X, Loader2, Filter } from 'lucide-react'
import { useState, useMemo } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

// ==================== Types ====================

export type LeaveType = 'annual' | 'sick' | 'personal' | 'marriage' | 'maternity' | 'paternity' | 'bereavement'
export type ReimbursementType = 'travel' | 'office' | 'meal' | 'transport' | 'other'

export interface BatchApprovalResult {
  success: string[]
  failed: Array<{ id: string; reason: string }>
}

export interface BatchApprovalItem {
  id: string
  type: string
}

export interface BatchApprovalToolbarProps {
  selectedIds: string[]
  entityType: 'leave' | 'reimbursement'
  /** Items with type information for filtering */
  items?: BatchApprovalItem[]
  onApprove: (ids: string[]) => Promise<BatchApprovalResult>
  onReject: (ids: string[], reason: string) => Promise<BatchApprovalResult>
  onClear: () => void
  onComplete?: (result: BatchApprovalResult) => void
  className?: string
}

// ==================== Component ====================

export function BatchApprovalToolbar({
  selectedIds,
  entityType,
  items,
  onApprove,
  onReject,
  onClear,
  onComplete,
  className,
}: BatchApprovalToolbarProps) {
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [result, setResult] = useState<BatchApprovalResult | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('all')

  const entityLabel = entityType === 'leave' ? '请假' : '报销'
  const typeLabels = entityType === 'leave' ? LEAVE_TYPE_LABELS : REIMBURSEMENT_TYPE_LABELS

  // Get unique types from items
  const availableTypes = useMemo(() => {
    if (!items || items.length === 0) { return [] }
    const types = new Set(items.filter(item => selectedIds.includes(item.id)).map(item => item.type))
    return Array.from(types)
  }, [items, selectedIds])

  // Filter selected IDs by type
  const filteredSelectedIds = useMemo(() => {
    if (typeFilter === 'all' || !items) {
      return selectedIds
    }
    return selectedIds.filter(id => {
      const item = items.find(i => i.id === id)
      return item?.type === typeFilter
    })
  }, [selectedIds, typeFilter, items])

  // Count by type for display
  const countByType = useMemo(() => {
    if (!items) { return {} }
    const counts: Record<string, number> = {}
    selectedIds.forEach(id => {
      const item = items.find(i => i.id === id)
      if (item) {
        counts[item.type] = (counts[item.type] || 0) + 1
      }
    })
    return counts
  }, [items, selectedIds])

  const handleApprove = () => {
    if (filteredSelectedIds.length === 0) { return }

    setIsApproving(true)
    void (async () => {
      try {
        const result = await onApprove(filteredSelectedIds)
        setResult(result)
        onComplete?.(result)
        if (result.failed.length === 0) {
          onClear()
          setTypeFilter('all')
        }
      } finally {
        setIsApproving(false)
      }
    })()
  }

  const handleReject = () => {
    if (filteredSelectedIds.length === 0 || !rejectReason.trim()) { return }

    setIsRejecting(true)
    void (async () => {
      try {
        const result = await onReject(filteredSelectedIds, rejectReason.trim())
        setResult(result)
        onComplete?.(result)
        if (result.failed.length === 0) {
          onClear()
          setShowRejectDialog(false)
          setRejectReason('')
          setTypeFilter('all')
        }
      } finally {
        setIsRejecting(false)
      }
    })()
  }

  const closeResultDialog = () => {
    setResult(null)
    if (result && result.failed.length === 0) {
      onClear()
      setTypeFilter('all')
    }
  }

  if (selectedIds.length === 0) {
    return null
  }

  const hasTypeFilter = items && items.length > 0 && availableTypes.length > 1

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between rounded-lg border bg-card p-3 shadow-sm',
          className
        )}
      >
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            已选择 <span className="text-primary">{selectedIds.length}</span> 项{entityLabel}申请
            {typeFilter !== 'all' && filteredSelectedIds.length !== selectedIds.length && (
              <span className="text-muted-foreground">
                {' '}(筛选后 <span className="text-primary">{filteredSelectedIds.length}</span> 项)
              </span>
            )}
          </span>
          <button
            onClick={() => {
              onClear()
              setTypeFilter('all')
            }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            取消选择
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Type Filter */}
          {hasTypeFilter && (
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 w-[140px]">
                  <SelectValue placeholder="筛选类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    全部类型 ({selectedIds.length})
                  </SelectItem>
                  {availableTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {typeLabels[type as keyof typeof typeLabels] || type} ({countByType[type] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <button
            onClick={handleApprove}
            disabled={isApproving || isRejecting || filteredSelectedIds.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            批量通过{filteredSelectedIds.length !== selectedIds.length && typeFilter !== 'all' ? ` (${filteredSelectedIds.length})` : ''}
          </button>
          <button
            onClick={() => setShowRejectDialog(true)}
            disabled={isApproving || isRejecting || filteredSelectedIds.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            批量拒绝{filteredSelectedIds.length !== selectedIds.length && typeFilter !== 'all' ? ` (${filteredSelectedIds.length})` : ''}
          </button>
        </div>
      </div>

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-lg">
            <h3 className="text-lg font-semibold">批量拒绝{entityLabel}申请</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              将拒绝 {filteredSelectedIds.length} 项{entityLabel}申请
              {typeFilter !== 'all' && (
                <span className="text-primary"> ({typeLabels[typeFilter as keyof typeof typeLabels] || typeFilter})</span>
              )}
              ，请输入拒绝原因
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="请输入拒绝原因"
              rows={3}
              className="mt-4 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRejectDialog(false)
                  setRejectReason('')
                }}
                disabled={isRejecting}
                className="rounded-md px-4 py-2 text-sm hover:bg-muted"
              >
                取消
              </button>
              <button
                onClick={handleReject}
                disabled={isRejecting || !rejectReason.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isRejecting && <Loader2 className="h-4 w-4 animate-spin" />}
                确认拒绝
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Dialog */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-lg">
            <h3 className="text-lg font-semibold">批量操作结果</h3>

            <div className="mt-4 space-y-3">
              {result.success.length > 0 && (
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="h-5 w-5" />
                  <span>成功处理 {result.success.length} 项</span>
                </div>
              )}

              {result.failed.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600">
                    <X className="h-5 w-5" />
                    <span>失败 {result.failed.length} 项</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/50 p-2">
                    {result.failed.map((item, index) => (
                      <div key={index} className="text-sm text-muted-foreground">
                        <span className="font-mono text-xs">{item.id.slice(0, 8)}...</span>
                        <span className="ml-2">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={closeResultDialog}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
